import { useCallback, useEffect, useState } from 'react';
import { Badge, Field, Note, Panel, Spinner, TableWrap } from '@wyocspay/ui';
import { ApiError, api } from './api.js';
import { money } from './format.js';

/**
 * EU tax reporting details.
 *
 * This screen exists because of Council Directive (EU) 2021/514 - DAC7 - which
 * obliges a platform operator to collect a specific set of details about
 * sellers resident in the EU and report them to a tax authority once a year.
 * Whether it binds WyoCSPay at all is genuinely unresolved; see
 * compliance/dac7-reporting.md.
 *
 * Two things shape the design.
 *
 * First, most merchants on this platform are not in the EU and never will be.
 * Asking a merchant in Wyoming for their date of birth and tax number with no
 * explanation is how you generate a support ticket that begins "why do you
 * need". So the page leads with whether it applies, says why it thinks so, and
 * only then asks.
 *
 * Second, almost none of this can be pre-filled from Stripe. Stripe holds an
 * address, a date of birth and a tax number for every verified connected
 * account, and returns almost none of them: on accounts where Stripe collects
 * the requirements - which is how this platform creates them, so that Stripe
 * carries the losses - a platform may read the name and the email and little
 * else. The tax number is not a permissions question at all: Stripe has no
 * field that returns it, for anybody. So it is asked here, once, and the form
 * says so rather than leaving the merchant wondering why they are typing it
 * twice.
 */

type Guidance = {
  relevant_activities: Array<{ key: string; label: string; description: string; citation: string }>;
  member_states: string[];
  de_minimis: { activity_count: number; consideration_eur: number; citation: string; note: string };
};

type MissingField = {
  field: string;
  label: string;
  citation: string;
  severity: 'required' | 'conditional' | 'where_available';
  note?: string;
};

type Profile = Awaited<ReturnType<typeof api.dac7>>['profile'];

const SEVERITY_TONE: Record<MissingField['severity'], 'fail' | 'warn' | 'neutral'> = {
  required: 'fail',
  conditional: 'warn',
  where_available: 'neutral',
};

const SEVERITY_LABEL: Record<MissingField['severity'], string> = {
  required: 'REQUIRED',
  conditional: 'IF IT APPLIES',
  where_available: 'IF YOU HAVE ONE',
};

/** A blank form state, so an uncollected profile still has fields to type into. */
function blank() {
  return {
    seller_type: '' as '' | 'INDIVIDUAL' | 'ENTITY',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    place_of_birth: '',
    place_of_birth_country: '',
    legal_name: '',
    business_registration_number: '',
    line1: '',
    line2: '',
    city: '',
    region: '',
    post_code: '',
    country: '',
    tin: '',
    tin_issued_by: '',
    second_tin: '',
    second_tin_issued_by: '',
    vat_number: '',
    financial_account_identifier: '',
    account_holder_name: '',
    permanent_establishments: '',
    activities: [] as string[],
  };
}

function fromProfile(profile: Profile): ReturnType<typeof blank> {
  const form = blank();
  if (!profile) return form;
  return {
    ...form,
    seller_type: profile.sellerType === 'UNKNOWN' ? '' : profile.sellerType,
    first_name: profile.firstName ?? '',
    last_name: profile.lastName ?? '',
    date_of_birth: profile.dateOfBirth ?? '',
    place_of_birth: profile.placeOfBirth ?? '',
    place_of_birth_country: profile.placeOfBirthCountry ?? '',
    legal_name: profile.legalName ?? '',
    business_registration_number: profile.businessRegistrationNumber ?? '',
    line1: profile.address.line1 ?? '',
    line2: profile.address.line2 ?? '',
    city: profile.address.city ?? '',
    region: profile.address.region ?? '',
    post_code: profile.address.postCode ?? '',
    country: profile.address.country ?? '',
    tin: profile.tins[0]?.tin ?? '',
    tin_issued_by: profile.tins[0]?.issuedBy ?? '',
    second_tin: profile.tins[1]?.tin ?? '',
    second_tin_issued_by: profile.tins[1]?.issuedBy ?? '',
    vat_number: profile.vatNumber ?? '',
    financial_account_identifier: profile.financialAccountIdentifier ?? '',
    account_holder_name: profile.accountHolderName ?? '',
    permanent_establishments: profile.permanentEstablishments.join(', '),
    activities: [...profile.relevantActivities],
  };
}

export function Dac7View() {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.dac7>> | null>(null);
  const [form, setForm] = useState(blank());
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await api.dac7();
      setData(next);
      setForm(fromProfile(next.profile));
      setShowForm(next.in_scope || Boolean(next.profile));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Note tone="danger">{error}</Note>;
  if (!data) return <Spinner label="Loading…" />;

  const guidance = data.guidance as Guidance;
  const missing = data.missing as MissingField[];
  const set = (key: keyof ReturnType<typeof blank>, value: string | string[]) => {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: value }));
  };

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      /**
       * Blank strings are sent as null rather than as "". A field the merchant
       * cleared should read as absent when the reporting rules ask whether it
       * was collected, not as a value that happens to be empty.
       */
      const orNull = (value: string) => (value.trim() === '' ? null : value.trim());
      const tins = [
        { tin: form.tin.trim(), issued_by: form.tin_issued_by.trim().toUpperCase() },
        { tin: form.second_tin.trim(), issued_by: form.second_tin_issued_by.trim().toUpperCase() },
      ].filter((t) => t.tin !== '' && /^[A-Z]{2}$/.test(t.issued_by));

      await api.saveDac7({
        ...(form.seller_type ? { seller_type: form.seller_type } : {}),
        first_name: orNull(form.first_name),
        last_name: orNull(form.last_name),
        date_of_birth: orNull(form.date_of_birth),
        place_of_birth: orNull(form.place_of_birth),
        place_of_birth_country: orNull(form.place_of_birth_country)?.toUpperCase() ?? null,
        legal_name: orNull(form.legal_name),
        business_registration_number: orNull(form.business_registration_number),
        address: {
          line1: orNull(form.line1),
          line2: orNull(form.line2),
          city: orNull(form.city),
          region: orNull(form.region),
          post_code: orNull(form.post_code),
          country: orNull(form.country)?.toUpperCase() ?? null,
        },
        tins,
        vat_number: orNull(form.vat_number),
        financial_account_identifier: orNull(form.financial_account_identifier),
        account_holder_name: orNull(form.account_holder_name),
        permanent_establishments: form.permanent_establishments
          .split(',')
          .map((c) => c.trim().toUpperCase())
          .filter((c) => /^[A-Z]{2}$/.test(c)),
        relevant_activities: form.activities as Array<
          'IMMOVABLE_PROPERTY_RENTAL' | 'PERSONAL_SERVICE' | 'SALE_OF_GOODS' | 'TRANSPORT_RENTAL'
        >,
      });
      setSaved(true);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  const isIndividual = form.seller_type === 'INDIVIDUAL';
  const isEntity = form.seller_type === 'ENTITY';

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {data.in_scope ? (
        <Note tone="warn" title="This looks like it applies to you">
          <p style={{ margin: '0 0 6px' }}>
            EU platform reporting rules (DAC7, Council Directive (EU) 2021/514) require us to hold a
            specific set of details about sellers connected to the EU, and to report them to a tax
            authority by 31 January each year. We have this account down as connected to the EU
            because:
          </p>
          <ul style={{ margin: '0 0 6px', paddingLeft: 18 }}>
            {data.signals.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
          <p style={{ margin: 0 }}>
            If that is wrong, tell us through Support and we will correct it rather than reporting
            you.
          </p>
        </Note>
      ) : (
        <Note title="We do not think this applies to your account">
          <p style={{ margin: '0 0 6px' }}>
            EU platform reporting (DAC7) covers sellers resident in an EU Member State, and sellers
            anywhere who let out property located in one. Nothing on your account points that way, so
            there is nothing for you to do here.
          </p>
          <button type="button" className="wc-btn wc-btn-sm" onClick={() => setShowForm(true)}>
            Fill it in anyway
          </button>
        </Note>
      )}

      {missing.length > 0 && (data.in_scope || data.profile) ? (
        <Panel title="What we still need">
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Detail</th>
                  <th>When it is needed</th>
                  <th>Where it comes from</th>
                </tr>
              </thead>
              <tbody>
                {missing.map((field) => (
                  <tr key={`${field.field}:${field.label}`}>
                    <td>
                      <strong>{field.label}</strong>
                      <div style={{ fontSize: 12, color: 'var(--wc-faint)' }}>{field.citation}</div>
                    </td>
                    <td>
                      <Badge tone={SEVERITY_TONE[field.severity]} icon={false}>
                        {SEVERITY_LABEL[field.severity]}
                      </Badge>
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{field.note ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Panel>
      ) : null}

      {showForm ? (
        <>
          <Panel title="Who the seller is">
            <Note>
              We ask for these rather than reading them from Stripe because Stripe does not give
              them back. On accounts where Stripe collects your verification documents - which is how
              your account is set up, so that Stripe carries the risk rather than us - a platform can
              read your name and little else. Your tax number is not a permissions question at all:
              Stripe accepts one and has no field that returns it to anybody.
            </Note>

            <Field label="Are you reporting as an individual or as a company?">
              <select
                className="wc-input"
                value={form.seller_type}
                onChange={(e) => set('seller_type', e.target.value)}
              >
                <option value="">Choose…</option>
                <option value="INDIVIDUAL">An individual, including a sole trader</option>
                <option value="ENTITY">A company, partnership, trust or foundation</option>
              </select>
            </Field>

            {isIndividual ? (
              <div className="wc-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                <Field label="First name">
                  <input className="wc-input" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
                </Field>
                <Field label="Last name">
                  <input className="wc-input" value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
                </Field>
                <Field label="Date of birth" hint="Required for an individual seller.">
                  <input
                    className="wc-input"
                    type="date"
                    value={form.date_of_birth}
                    onChange={(e) => set('date_of_birth', e.target.value)}
                  />
                </Field>
                <Field
                  label="Town of birth"
                  hint="Only needed if you have no tax number to give us."
                >
                  <input className="wc-input" value={form.place_of_birth} onChange={(e) => set('place_of_birth', e.target.value)} />
                </Field>
                <Field label="Country of birth" hint="Two-letter code, e.g. IE.">
                  <input
                    className="wc-input"
                    maxLength={2}
                    value={form.place_of_birth_country}
                    onChange={(e) => set('place_of_birth_country', e.target.value.toUpperCase())}
                  />
                </Field>
              </div>
            ) : null}

            {isEntity ? (
              <div className="wc-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                <Field label="Legal name" hint="As registered, not the trading name.">
                  <input className="wc-input" value={form.legal_name} onChange={(e) => set('legal_name', e.target.value)} />
                </Field>
                <Field
                  label="Business registration number"
                  hint="The number your company register issued. Not the VAT number and not the tax number."
                >
                  <input
                    className="wc-input"
                    value={form.business_registration_number}
                    onChange={(e) => set('business_registration_number', e.target.value)}
                  />
                </Field>
                <Field
                  label="Permanent establishments in the EU"
                  hint="Two-letter country codes, comma separated. Leave blank if none."
                >
                  <input
                    className="wc-input"
                    value={form.permanent_establishments}
                    onChange={(e) => set('permanent_establishments', e.target.value)}
                  />
                </Field>
              </div>
            ) : null}
          </Panel>

          <Panel title={isEntity ? 'Registered office address' : 'Home address'}>
            <Note>
              {isEntity
                ? 'The address of your registered office, which may not be where you trade from.'
                : 'Your primary residence. This decides which country we would report to.'}
            </Note>
            <div className="wc-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <Field label="Address line 1">
                <input className="wc-input" value={form.line1} onChange={(e) => set('line1', e.target.value)} />
              </Field>
              <Field label="Address line 2">
                <input className="wc-input" value={form.line2} onChange={(e) => set('line2', e.target.value)} />
              </Field>
              <Field label="Town or city">
                <input className="wc-input" value={form.city} onChange={(e) => set('city', e.target.value)} />
              </Field>
              <Field label="Region or county">
                <input className="wc-input" value={form.region} onChange={(e) => set('region', e.target.value)} />
              </Field>
              <Field label="Post code">
                <input className="wc-input" value={form.post_code} onChange={(e) => set('post_code', e.target.value)} />
              </Field>
              <Field label="Country" hint="Two-letter code, e.g. DE.">
                <input
                  className="wc-input"
                  maxLength={2}
                  value={form.country}
                  onChange={(e) => set('country', e.target.value.toUpperCase())}
                />
              </Field>
            </div>
          </Panel>

          <Panel title="Tax numbers">
            <Note>
              A tax identification number and the country that issued it. If you are tax resident in
              more than one Member State, give us both - the second one is how the second country
              knows to expect you.
            </Note>
            <div className="wc-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <Field label="Tax identification number (TIN)">
                <input className="wc-input" value={form.tin} onChange={(e) => set('tin', e.target.value)} />
              </Field>
              <Field label="Issued by" hint="Two-letter country code.">
                <input
                  className="wc-input"
                  maxLength={2}
                  value={form.tin_issued_by}
                  onChange={(e) => set('tin_issued_by', e.target.value.toUpperCase())}
                />
              </Field>
              <Field label="Second TIN" hint="Only if you have one.">
                <input className="wc-input" value={form.second_tin} onChange={(e) => set('second_tin', e.target.value)} />
              </Field>
              <Field label="Issued by">
                <input
                  className="wc-input"
                  maxLength={2}
                  value={form.second_tin_issued_by}
                  onChange={(e) => set('second_tin_issued_by', e.target.value.toUpperCase())}
                />
              </Field>
              <Field label="VAT number" hint="Only if you are VAT registered.">
                <input className="wc-input" value={form.vat_number} onChange={(e) => set('vat_number', e.target.value)} />
              </Field>
            </div>
          </Panel>

          <Panel title="What you sell">
            <Note title="Only four things count">
              The rules list four kinds of activity and nothing else. Tick what applies. This matters:
              a seller of goods under {guidance.de_minimis.activity_count} sales and{' '}
              {guidance.de_minimis.consideration_eur.toLocaleString('en-GB')} EUR a year is not
              reported at all, while there is no such threshold for services - one job of any value
              is reportable.
            </Note>
            <div style={{ display: 'grid', gap: 10 }}>
              {guidance.relevant_activities.map((activity) => (
                <label key={activity.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={form.activities.includes(activity.key)}
                    onChange={(e) =>
                      set(
                        'activities',
                        e.target.checked
                          ? [...form.activities, activity.key]
                          : form.activities.filter((k) => k !== activity.key),
                      )
                    }
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <strong style={{ fontSize: 14 }}>{activity.label}</strong>
                    <span style={{ display: 'block', fontSize: 13, color: 'var(--wc-muted)' }}>
                      {activity.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            {form.activities.includes('IMMOVABLE_PROPERTY_RENTAL') ? (
              <Note tone="warn" title="Property letting needs more from you">
                Every property you let has to be reported by address, with its land registration
                number where one exists. Send us the list through Support and we will record it -
                there is no way to type it in here yet.
              </Note>
            ) : null}
          </Panel>

          <Panel title="Where the money goes">
            <Note>
              The account your payouts land in. We can see the last four digits of it from Stripe and
              nothing more, so the full number has to come from you. If you have no reason to give it,
              say nothing: this one is reportable only if we actually hold it.
            </Note>
            <div className="wc-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <Field label="IBAN or account identifier">
                <input
                  className="wc-input"
                  value={form.financial_account_identifier}
                  onChange={(e) => set('financial_account_identifier', e.target.value)}
                />
              </Field>
              <Field
                label="Account holder name"
                hint="Only if the account is not in your own name."
              >
                <input
                  className="wc-input"
                  value={form.account_holder_name}
                  onChange={(e) => set('account_holder_name', e.target.value)}
                />
              </Field>
            </div>
          </Panel>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button type="button" className="wc-btn wc-btn-primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save these details'}
            </button>
            {saved ? <Badge tone="ok">Saved</Badge> : null}
            <span style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>
              Due diligence has to be complete by 31 December of the year being reported.
            </span>
          </div>
        </>
      ) : null}

      <Dac7Figures />
    </div>
  );
}

/**
 * What we would report about this merchant, per quarter and per currency.
 *
 * Separate from the form because it is not something the merchant fills in, and
 * because Section III(A)(5) of the Directive requires the same figures to be
 * given to the seller in any event. Showing them all year rather than once in
 * January means a disagreement surfaces while the records still exist.
 *
 * Currencies are never added together. A merchant who charged in euros and
 * dollars sees two lines, because the total of the two is not a number that
 * corresponds to any amount of money.
 */
function Dac7Figures() {
  const [year, setYear] = useState(new Date().getUTCFullYear() - 1);
  const [aggregate, setAggregate] = useState<Awaited<ReturnType<typeof api.dac7Aggregate>>['aggregate'] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setAggregate((await api.dac7Aggregate(year)).aggregate);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      }
    })();
  }, [year]);

  const years = [0, 1, 2].map((n) => new Date().getUTCFullYear() - 1 - n);

  return (
    <Panel
      title="What we would report"
      actions={
        <select
          className="wc-input"
          style={{ width: 'auto' }}
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      }
    >
      {error ? <Note tone="danger">{error}</Note> : null}
      {!aggregate ? (
        <Spinner label="Loading…" />
      ) : aggregate.quarters.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--wc-muted)', fontSize: 14 }}>
          Nothing settled on this account in {year}, so there would be nothing to report.
        </p>
      ) : (
        <>
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Quarter</th>
                  <th>Currency</th>
                  <th style={{ textAlign: 'right' }}>Charged</th>
                  <th style={{ textAlign: 'right' }}>Refunded</th>
                  <th style={{ textAlign: 'right' }}>Our fee</th>
                  <th style={{ textAlign: 'right' }}>Reported to you</th>
                  <th style={{ textAlign: 'right' }}>Activities</th>
                </tr>
              </thead>
              <tbody>
                {aggregate.quarters.map((q) => (
                  <tr key={`${q.quarter}:${q.currency}`}>
                    <td>Q{q.quarter}</td>
                    <td>{q.currency.toUpperCase()}</td>
                    <td style={{ textAlign: 'right' }}>{money(q.gross, q.currency)}</td>
                    <td style={{ textAlign: 'right' }}>{money(q.refunded, q.currency)}</td>
                    <td style={{ textAlign: 'right' }}>{money(q.feesWithheld, q.currency)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {money(q.consideration, q.currency)}
                    </td>
                    <td style={{ textAlign: 'right' }}>{q.activityCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
          <Note>
            The reported figure is what reached you: the charge less anything refunded and less our
            platform fee. Stripe's own processing fee is not deducted here because it is charged to
            you by Stripe, not withheld by us. Each currency stands on its own - there is no combined
            total, because there is no such amount.
          </Note>
        </>
      )}
    </Panel>
  );
}
